import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/api";

const Login = () => {
  const { t, lang } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      // EVH-1 — if the backend issued a soft-login token, the user is
      // unverified and we must route them to the pending-verification
      // page. AuthContext exposes the raw flag via window.__lastLoginSoft
      // (set by login()); fall back to checking user.email_verified.
      const softLogin = window.__lastLoginSoft === true;
      if (softLogin || (u && u.email_verified === false && window.__evhRequired === true)) {
        navigate("/verify-email-pending");
        return;
      }
      toast.success(t("phase2.welcomeBackToast"));
      if (u.must_change_password) navigate("/change-password");
      else if (u.role === "institutional_observer") navigate("/admin/stats-advanced");
      else if (u.role === "admin" || u.role === "staff" || u.role === "qa_manager") navigate("/admin");
      else navigate("/app");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="tsw-section py-16 max-w-md">
        <div className="mb-8">
          <div className="tsw-overline mb-2">{t("common.login")}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">{t("auth.loginTitle")}</h1>
          <p className="text-slate-600 mt-2">{t("auth.loginSub")}</p>
        </div>
        <form onSubmit={submit} className="tsw-card p-6 sm:p-8 space-y-5" data-testid="login-form">
          <div>
            <label className="tsw-label">{t("auth.email")}</label>
            <input type="email" required className="tsw-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div>
            <label className="tsw-label">{t("auth.password")}</label>
            <input type="password" required className="tsw-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <button type="submit" className="tsw-btn-primary w-full" disabled={loading} data-testid="login-submit">
            {loading ? t("common.loading") : t("auth.submitLogin")}
          </button>
          <div className="text-center text-sm">
            <Link
              to="/forgot-password"
              className="text-emerald-700 font-medium hover:text-emerald-800"
              data-testid="login-to-forgot"
            >
              {t("phase2.forgotPasswordLink")}
            </Link>
          </div>
          <div className="text-center text-sm text-slate-500">
            {t("auth.noAccount")} <Link to="/register" className="text-emerald-700 font-medium" data-testid="login-to-register">{t("common.register")}</Link>
          </div>
        </form>
      </div>
    </PublicLayout>
  );
};

export default Login;
